//imports here
import validateMandatoryFields from "../../components/commonfunctions/validateMandatoryFields";
import LoginModel from "../../components/Models/LoginModel";
import { ChangeEvent, useEffect, useState } from "react";
import { motion } from "framer-motion";
import PasswordField from "../../components/inputfields/PasswordField";
import useApi from "../../services/useApi";
import { useDispatch } from "react-redux";
import { showToast } from "../../components/toastMessage/toastSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import useTranslation from "../../components/CustomHooks/useTranslation";
import { contents } from "./AuthContents";
import { getFirefoxCssFix } from "../../components/commonfunctions/getFirefoxCssFix";

export default function SetNewPassword() {
  //declared states that are used in the forgot password feature
  const InitialState: { [key: string]: string } = {
    new_password: "",
    confirm_password: "",
  };
  const [state, setState] = useState(InitialState);
  const [error, setError] = useState<{ [key: string]: string }>(InitialState);
  const [loading, setLoading] = useState(false);
  const { PatchAPI, PostAPI } = useApi();
  const dispatch = useDispatch();
  const [urlParams, setUrlParams] = useSearchParams();
  const navigate = useNavigate();
  const { translate, notEnglish } = useTranslation(
    contents?.["Forgot password"]
  );
  const [verifyloading, setVerifyLoading] = useState(false)

  // object for input tags
  const setNewPassword = [
    {
      labelName: "New Password",
      id: "new_password",
      value: state?.new_password,
    },
    {
      labelName: "Confirm Password",
      id: "confirm_password",
      value: state?.confirm_password,
    },
  ];

  // submit function to update the new passwords
  const handleSubmit = async () => {
    //regex for the password
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^\w\s]).{6,16}$/;

    //making states null and empty
    setError(InitialState);

    // checking whether the fields are empty
    let mandatoryFields = ["new_password", "confirm_password"];
    let error = validateMandatoryFields(state, mandatoryFields);
    if (Object.keys(error).length > 0) {
      setError(error);
      return;
    }

    //checking whether keys are equal adn also the rules of password
    if (state?.new_password !== state?.confirm_password) {
      setError({
        new_password: " ",
        confirm_password: "Passwords do not match", // pragma: allowlist secret
      });
      return;
    }
    if (!passwordRegex.test(state?.new_password)) {
      setError({
        new_password: "Invalid password", // pragma: allowlist secret
      });
      return;
    }

    //creating api payload
    let body = {
      token: urlParams.get("token"),
      password: state?.new_password,
      confirm_password: state?.confirm_password,
    };
    setLoading(true);
    let response;
    response = await PatchAPI("admins/reset-password", body, false);
    if (!response.error) {
      navigate("/password-changed");
    } else {
      dispatch(
        showToast({
          type: "error",
          message: response?.data?.message || "Something went wrong",
        })
      );
    }
    setLoading(false);
  };
  const PASSWORD_REQUIREMENTS = [
    {
      test: (password: string) => password.length >= 6 && password.length <= 16,
      message: "Minimum 6 and maximum 16 Characters",
    },
    {
      test: (password: string) =>
        /[!@#$%^&*(),.?":{}|<>_+=\-\[\]\\/;:'"~`]/.test(password),
      message: "Atleast 1 Special Character (e.g. !@#$)",
    },
    {
      test: (password: string) => /\d/.test(password),
      message: "Atleast 1 Number",
    },
    {
      test: (password: string) => /[A-Z]/.test(password),
      message: "Atleast 1 Uppercase",
    },
    {
      test: (password: string) => /[a-z]/.test(password),
      message: "Atleast 1 Lowercase",
    },
  ];

  //function to handle the change in input fields
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;

    // Clear the error if passwords match or the user starts typing again
    if (error.confirm_password === "Passwords do not match") { // pragma: allowlist secret
      setError(InitialState);
    }

    // Update state and error for the input field
    setState((prev) => ({
      ...prev,
      [id]: value,
    }));
    setError((prev) => ({
      ...prev,
      [id]: "",
    }));
  };

  //function to verify token while resetting password
  const verfiyToken = async (token: string) => {
    setVerifyLoading(true)
    //payload
    let payload = { token: token }
    let response = await PostAPI(`admins/verify-token`, payload)
    if (response.error) {
      navigate('/expired');
    }
    setVerifyLoading(false)
  }

  /* The `useEffect` hook in the code snippet is used to perform side effects in function components.
    In this specific case: */
  useEffect(() => {
    const token = urlParams.get("token");
    if (token === null || token === undefined) {
      navigate("/");
    } else {
      verfiyToken(token);
    }
  }, []);
  return (
    (!verifyloading && <LoginModel
      heading={translate("Set New Password")}
      instruction={translate(
        "Please set a new password to regain access to your account"
      )}
      handleSubmit={handleSubmit}
      loading={loading}
      forgotPassword={false}
      mainImg="zood_pay_sidebar_img"
      loginFlow
      buttonName={translate("Reset")}
      testId="reset-btn"
    >
      <div
        className={`w-full ${state?.new_password.length > 0 ? "h-[350px]" : ""
          }`}
      >
        {setNewPassword?.map((item, index) => (
          <div className="mt-5 " key={index}>
            <PasswordField
              inputItem={{ labelName: item?.labelName, id: item?.id }}
              errorMessage={error?.[item?.id]}
              onChange={(e) => handleInputChange(e)}
            // maxLength={6}
            />
            {index === 0 && state?.new_password.length > 0 && (
              <div className="mt-3">
                <p className="mb-3">{translate("Password should contain:")}</p>
                <div className={`max-h-[80px] pl-1 pr-5 overflow-y-scroll overflow-x-hidden ${getFirefoxCssFix('scroll')} scroll-auto`}>
                  <ul className={`list-disc ${notEnglish ? "mr-4" : "ml-4"} `}>
                    {PASSWORD_REQUIREMENTS.map(({ test, message }) => (
                      <motion.li
                        key={message}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{
                          opacity: 1,
                          x: 0,
                          color: state.new_password
                            ? test(state.new_password)
                              ? "#28a745"
                              : "#000000"
                            : "#000000",
                        }}
                        transition={{ duration: 0.5 }}
                        className="mb-2 body2"
                      >
                        {translate(message)}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </LoginModel>)
  );
}
